"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { SubmitButton } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SkeletonBox, SkeletonContainer } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { useMarkAllRead } from "../../hooks/use-mark-all-read";
import { useNotifications } from "../../hooks/use-notifications";
import { NotificationListEmpty } from "./empty";
import { NotificationItem } from "./item";
import { NotificationListPending } from "./pending";
import {
  NOTIFICATION_TAB_ITEMS,
  type NotificationTab,
} from "./tab-items";

const EMPTY_COPY: Record<
  NotificationTab,
  { title: string; description: string }
> = {
  unread: {
    title: "You're all caught up",
    description: "You have no unread notifications right now.",
  },
  read: {
    title: "No read notifications",
    description: "Notifications you mark as read will show up here.",
  },
  all: {
    title: "You have no notifications",
    description: "You have no notifications right now.",
  },
};

export function NotificationsPanelContent() {
  const [tab, setTab] = useState<NotificationTab>("unread");
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending } =
    useNotifications(tab);
  const markAllRead = useMarkAllRead();
  const viewportRef = useRef<HTMLDivElement | null>(null);

  const pages = data?.pages ?? [];
  const notifications = pages.flatMap((p) => p.items);
  const isInitialLoading = isPending && data === undefined;
  const isEmpty = notifications.length === 0;
  const notificationCountLabel = `${notifications.length} ${
    notifications.length === 1 ? "Notification" : "Notifications"
  }`;

  const maybeFetchNextPage = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport || !hasNextPage || isFetchingNextPage) return;

    const remaining =
      viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
    if (remaining <= 96) void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    viewport.addEventListener("scroll", maybeFetchNextPage, {
      passive: true,
    });
    maybeFetchNextPage();

    return () => {
      viewport.removeEventListener("scroll", maybeFetchNextPage);
    };
  }, [maybeFetchNextPage]);

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-surface-secondary">
      <Tabs
        value={tab}
        onValueChange={(v) => setTab(v as NotificationTab)}
        size="sm"
        className="flex shrink-0 border-b border-border bg-surface-primary px-3 py-3 dark:bg-surface-inset"
      >
        <TabsList aria-label="Notification view">
          {NOTIFICATION_TAB_ITEMS.map((item) => (
            <TabsTrigger key={item.value} value={item.value}>
              <Icon name={item.icon} className="relative text-foreground" />
              {item.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {isInitialLoading ? (
        <>
          <div className="flex shrink-0 items-center justify-between px-3 pb-2.5 pt-3">
            <SkeletonContainer>
              <SkeletonBox className="h-3.5 w-24" />
            </SkeletonContainer>
            <SkeletonContainer>
              <SkeletonBox tone="muted" className="h-4 w-12" />
            </SkeletonContainer>
          </div>

          <ScrollArea
            className="flex-1 min-h-0"
            contentClassName="space-y-2 px-3 pb-3"
            constrainContentWidth
          >
            <NotificationListPending />
            <NotificationListPending />
            <NotificationListPending />
          </ScrollArea>
        </>
      ) : isEmpty ? (
        <NotificationListEmpty
          title={EMPTY_COPY[tab].title}
          description={EMPTY_COPY[tab].description}
        />
      ) : (
        <ScrollArea
          className="flex-1 min-h-0"
          contentClassName="space-y-2 p-3"
          constrainContentWidth
          viewportRef={viewportRef}
        >
          <div className="flex shrink-0 items-center justify-between">
            <h2 className="text-xs font-medium text-heading">
              {notificationCountLabel}
            </h2>
            {tab === "unread" && (
              <SubmitButton
                type="button"
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
                loading={markAllRead.isPending}
                aria-label="Mark all notifications as read"
                variant="muted-ghost"
                size="text-xs"
              >
                Mark all read
              </SubmitButton>
            )}
          </div>

          {notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
            />
          ))}
          {isFetchingNextPage && (
            <>
              <NotificationListPending />
              <NotificationListPending />
            </>
          )}
        </ScrollArea>
      )}
    </div>
  );
}

export { NotificationItem } from "./item";
export { NotificationListEmpty } from "./empty";
export { NotificationListPending } from "./pending";
